import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Stack,
  Typography,
  CircularProgress,
  alpha
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FlagIcon from "@mui/icons-material/Flag";
import { InteractionOrder, Interaction, InteractionType } from "../types/interactions";
import { InteractionRow } from "./InteractionRow";
import { getInteractionKey } from "../utils/interactionKey";

interface InteractionTableProps {
  orders: InteractionOrder[];
  taggedOrders?: InteractionOrder[];
  interactions: Record<string, Interaction>;
  isLoading: boolean;
  error: string | null;
  statusFilter: string;
  useClosedTabOrderLayout?: boolean;
  interactionTypes: InteractionType[];
  onInteractionTypeChange: (
    orderNumber: string,
    type: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  onInteractionNoteChange: (
    orderNumber: string,
    note: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  onPreInstructionsChange: (
    orderNumber: string,
    instructions: string,
    contactId: number,
    settlementDate?: string | null
  ) => void;
  onSkipWithReason: (orderNumber: string, reason: string, contactId: number, settlementDate?: string | null) => void;
  onUndoInteraction: (orderNumber: string, contactId: number) => void;
  onClosedConfirmationChange: (orderNumber: string, isConfirmed: boolean) => void;
  getStatusColor: (status: string) => string;
  orderBy: "orderDate" | "contactName" | null;
  orderDirection: "asc" | "desc";
  onSort: (column: "orderDate" | "contactName") => void;
  onTagOrderContacts?: (orderNumber: string) => void;
  onUntagContact?: (orderNumber: string, contactId: number) => void;
}

export const InteractionTable = ({
  orders,
  taggedOrders = [],
  interactions,
  isLoading,
  error,
  statusFilter,
  useClosedTabOrderLayout = false,
  interactionTypes,
  onInteractionTypeChange,
  onInteractionNoteChange,
  onPreInstructionsChange,
  onSkipWithReason,
  onUndoInteraction,
  onClosedConfirmationChange,
  getStatusColor,
  orderBy,
  orderDirection,
  onSort,
  onTagOrderContacts,
  onUntagContact
}: InteractionTableProps) => {
  const theme = useTheme();
  const columnCount = statusFilter === "Closed" ? 10 : 9;

  if (isLoading) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Loading interactions...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" fontWeight={700} color="error">
            Error Loading Data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (orders.length === 0 && taggedOrders.length === 0 && !isLoading) {
    return (
      <Box sx={{ p: 10, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                theme.palette.primary.main,
                0.12
              )} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Typography variant="h3">📋</Typography>
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            No Orders Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
            There are no {statusFilter.toLowerCase()} orders for the selected date
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        overflow: "auto"
      }}
    >
      <Table sx={{ tableLayout: "fixed", minWidth: 1400 }}>
        <TableHead>
          <TableRow
            sx={{
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              borderBottom: `2px solid ${theme.palette.divider}`,
              "& th": {
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                py: 1.5,
                borderBottom: "none"
              }
            }}
          >
            <TableCell width={useClosedTabOrderLayout ? "135px" : "100px"}>
              <TableSortLabel
                active={orderBy === "orderDate"}
                direction={orderBy === "orderDate" ? orderDirection : "asc"}
                onClick={() => onSort("orderDate")}
              >
                Order
              </TableSortLabel>
            </TableCell>
            <TableCell width="60px">Type</TableCell>
            <TableCell width="260px">
              <TableSortLabel
                active={orderBy === "contactName"}
                direction={orderBy === "contactName" ? orderDirection : "asc"}
                onClick={() => onSort("contactName")}
              >
                Client
              </TableSortLabel>
            </TableCell>
            <TableCell width="280px">Property</TableCell>
            <TableCell width="140px">Contact Info</TableCell>
            <TableCell width="90px">Status</TableCell>
            {statusFilter === "Closed" && (
              <TableCell width="80px" align="center">
                Confirmed
              </TableCell>
            )}
            <TableCell width="100px" align="right">
              Amount
            </TableCell>
            <TableCell
              width="350px"
              sx={{
                borderRight: `2px solid ${theme.palette.divider}`
              }}
            >
              Pre Instructions
            </TableCell>
            <TableCell sx={{ width: { xs: 300, md: "auto" } }}>Interaction</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => {
            const orderNumber = order.orderNumber || "";
            const contactId = order.phonebookContactId || 0;
            const key = getInteractionKey(orderNumber, contactId);

            return (
              <InteractionRow
                key={key}
                order={order}
                interaction={interactions[key]}
                statusFilter={statusFilter}
                useClosedTabOrderLayout={useClosedTabOrderLayout}
                interactionTypes={interactionTypes}
                onInteractionTypeChange={onInteractionTypeChange}
                onInteractionNoteChange={onInteractionNoteChange}
                onPreInstructionsChange={onPreInstructionsChange}
                onSkipWithReason={onSkipWithReason}
                onUndoInteraction={onUndoInteraction}
                onClosedConfirmationChange={onClosedConfirmationChange}
                getStatusColor={getStatusColor}
                onTagOrderContacts={onTagOrderContacts}
                onUntagContact={onUntagContact}
              />
            );
          })}
          {taggedOrders.length > 0 && (
            <>
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                    py: 1.25
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FlagIcon sx={{ fontSize: "1rem", color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                      Flagged follow-ups
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
              {taggedOrders.map((order) => {
                const orderNumber = order.orderNumber || "";
                const contactId = order.phonebookContactId || 0;
                const key = getInteractionKey(orderNumber, contactId);

                return (
                  <InteractionRow
                    key={`tagged-${key}`}
                    order={order}
                    interaction={interactions[key]}
                    statusFilter={statusFilter}
                    useClosedTabOrderLayout={useClosedTabOrderLayout}
                    interactionTypes={interactionTypes}
                    onInteractionTypeChange={onInteractionTypeChange}
                    onInteractionNoteChange={onInteractionNoteChange}
                    onPreInstructionsChange={onPreInstructionsChange}
                    onSkipWithReason={onSkipWithReason}
                    onUndoInteraction={onUndoInteraction}
                    onClosedConfirmationChange={onClosedConfirmationChange}
                    getStatusColor={getStatusColor}
                    isTaggedSection
                    onTagOrderContacts={onTagOrderContacts}
                    onUntagContact={onUntagContact}
                  />
                );
              })}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
